import { urlParams } from '@/app/urlParams';
import type {
  CurrentSessionResponse,
  GameConfigResponse,
  GameOverRequest,
  GameOverResponse,
  GameStartRequest,
  GameStartResponse,
  NextSequenceRequest,
  NextSequenceResponse,
  TimeSyncResponse,
  ValidateRequest,
  ValidateResponse,
} from '@/types/api.types';
import { appConfig } from '@app/config/appConfig';

import { gameEngine } from '../engine/game-engine';

/**
 * Returns the stage ID to use for API calls.
 */
export function getStageId(): string {
  return urlParams.stageId || appConfig.socket.stageId;
}

export const gameApi = {
  getConfig: (stageId: string): Promise<GameConfigResponse> => {
    return Promise.resolve({ ...gameEngine.getConfig(), stageId });
  },

  start: (body: GameStartRequest): Promise<GameStartResponse> => {
    return gameEngine.startSession({
      ...body,
      enableTimer: urlParams.timer,
      timeLimit: urlParams.timeLimit ?? undefined,
    });
  },

  nextSequence: (body: NextSequenceRequest): Promise<NextSequenceResponse> =>
    gameEngine.nextSequence(body),

  prevSequence: (body: NextSequenceRequest): Promise<NextSequenceResponse> =>
    gameEngine.prevSequence(body),

  validate: (body: ValidateRequest): Promise<ValidateResponse> =>
    gameEngine.validateMove(body),

  gameOver: (body: GameOverRequest): Promise<GameOverResponse> =>
    gameEngine.endGame(body),

  timeSync: (_gameSessionId: string): Promise<TimeSyncResponse> => {
    const state = gameEngine.getState();
    const now = new Date().toISOString();
    return Promise.resolve({ 
      startTime: state ? new Date(state.startedAtMs).toISOString() : now, 
      endTime: state?.enableTimer ? new Date(state.expiryAtMs).toISOString() : now, 
      serverNow: now 
    });
  },

  currentSession: (_stageId: string, _sessionId: string): Promise<CurrentSessionResponse> =>
    gameEngine.currentSession(),
};
